/**
 * Returns an object composed from array of own enumerable string keyed-value pairs.
 * [['a', 1], ['b', 2]] => { 'a': 1, 'b': 2 }
 * @param {array} pairs array of pairs we want to convert.
 *
 * @return {object}
 */
function fromPairs(pairs) {
    let index = -1;
    const length = pairs == null ? 0 : pairs.length;
    const result = {};

    // eslint-disable-next-line no-plusplus
    while (++index < length) {
        const pair = pairs[index];
        result[pair[0]] = pair[1];
    }
    return result;
}

/**
 * Get parameter from URL.
 *
 * @param  {string} queryString   Full query string.
 * @param  {string} param         Parameter name.
 *
 * @return {string} Parameter value.
 */
function getParam(urlString, param) {
    try {
        const url = new URL(urlString);
        return url.searchParams.get(param);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        return '';
    }
}

/**
 * Split query params and build object out of them
 *
 * @param {string} url Full url string, eg. http://example.com?id=test&name=example
 *
 * @return {object}
 */
function parseURL(url) {
    const query = url.substring(url.indexOf('?') + 1);

    if (!query) {
        return false;
    }

    return fromPairs(
        query.split('&').map((params) => {
            const p = params.split('=');
            return [p[0], decodeURIComponent(p[1])];
        })
    );
}

/**
 * Helps build parameter query string parameters.
 *
 * @param {boolean} encode Should we encode?
 * @param {string}  key     Parameter key.
 * @param {string}  value   Parameter value.
 *
 * @return {string}
 */
function queryStrParam(encode, key, value) {
    return encode ? `${key}=${encodeURIComponent(value)}` : `${key}=${value}`;
}

/**
 * Build parameters for request
 * @param {object}  params            Parameters object.
 * @param {boolean} skipEmpty         Skip empty parameters.
 * @param {boolean} encode            Flag to encode value.
 *
 * @returns {string}
 */
function buildParams(params, skipEmpty, encode = false) {
    let constructParams = Object.entries(params);
    constructParams = constructParams.map((value) => {
        const valid = !skipEmpty || (value[0] && value[1]);

        if (valid) {
            if (Array.isArray(value[1])) {
                return value[1].map((element) => queryStrParam(encode, value[0], element)).join('&');
            }

            return queryStrParam(encode, value[0], value[1]);
        }

        return '';
    });

    constructParams = skipEmpty ? constructParams.filter(Boolean) : constructParams;

    constructParams = constructParams.join('&');

    return `?${constructParams}`;
}

/**
 *
 * @param queryString
 * @param key
 * @param value
 * @param paramsToRemove
 * @returns {string}
 */
function replaceUrlParam(queryString, param, paramsToRemove = []) {
    const params = new URLSearchParams(queryString);

    if (param) {
        params.set(Object.keys(param)[0], Object.values(param)[0]);
    }

    paramsToRemove.forEach((paramToRemove) => {
        params.delete(paramToRemove);
    });

    return `?${params.toString()}`;
}

/**
 * Append Parameters.
 * @param  {string} urlBase      The URL base to append parameters to.
 * @param  {string} paramString  A query string to append.
 * @return {string}              Parameter string appended to the given URL.
 */
// eslint-disable-next-line prefer-arrow-callback
function appendParams(urlBase, paramString = '') {
    const base = urlBase.replace(/\?$/, '');
    const sep = /\?/.test(base) ? '&' : '?';

    return `${base}${sep}${paramString?.replace(/^[?&]/, '') || ''}`;
}

export { getParam, parseURL, buildParams, replaceUrlParam, appendParams };
